<template>
  <section class="section reveal-section" id="contenido" ref="sectionRef">
    <div class="section-header">
      <span class="section-label">Nuestro Contenido</span>
      <h2 class="section-title">Recursos y Medios</h2>
      <p class="section-subtitle">Mantente informado e inspirado con nuestros boletines, blog, podcasts y más</p>
    </div>

    <div class="content-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        {{ tab.emoji }} {{ tab.label }}
      </button>
    </div>

    <!-- Boletines -->
    <div class="tab-content" v-show="activeTab === 'newsletter'">
      <div class="cards-grid">
        <div class="card-elevated" v-for="(item, i) in newsletters" :key="i">
          <div class="card-content">
            <div class="newsletter-icon">{{ item.icon }}</div>
            <h4 class="card-title">{{ item.title }}</h4>
            <p class="card-text">{{ item.description }}</p>
            <button class="card-action" style="background:var(--dorado);color:white;" @click="showToast(item.toast, 'success')">
              {{ item.action }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Blog -->
    <div class="tab-content" v-show="activeTab === 'blog'">
      <div class="cards-grid">
        <div class="card-elevated" v-for="(post, i) in blogPosts" :key="i">
          <div class="card-image" :style="{ background: post.gradient }">
            <i :class="post.icon"></i>
          </div>
          <div class="card-content">
            <div class="card-date">{{ post.date }}</div>
            <h4 class="card-title">{{ post.title }}</h4>
            <p class="card-text">{{ post.excerpt }}</p>
            <a href="#" class="read-more" @click.prevent="showToast('Abriendo artículo...', 'info')">Leer más →</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Actividades -->
    <div class="tab-content" v-show="activeTab === 'activities'">
      <div class="cards-grid">
        <div class="card-elevated" v-for="(act, i) in activities" :key="i">
          <div class="card-image" :style="{ background: act.gradient }">
            <i :class="act.icon"></i>
          </div>
          <div class="card-content">
            <span class="card-tag" :style="{ background: act.tagColor }">{{ act.tag }}</span>
            <h4 class="card-title">{{ act.title }}</h4>
            <p class="card-text">{{ act.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Podcasts -->
    <div class="tab-content" v-show="activeTab === 'podcasts'">
      <div class="cards-grid">
        <div class="card-elevated" v-for="(pod, i) in podcasts" :key="i" style="border-top-color:var(--rosa)">
          <div class="card-image" style="background:linear-gradient(135deg,var(--rosa),var(--dorado))">
            <i :class="pod.icon"></i>
          </div>
          <div class="card-content">
            <div class="card-date">{{ pod.duration }}</div>
            <h4 class="card-title">{{ pod.title }}</h4>
            <p class="card-text">{{ pod.description }}</p>
            <button class="card-action" style="background:var(--rosa);color:white;" @click="playMedia(pod.title)">
              <i class="fas fa-play-circle"></i> Escuchar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Videos -->
    <div class="tab-content" v-show="activeTab === 'videos'">
      <div class="cards-grid">
        <div class="card-elevated" v-for="(vid, i) in videos" :key="i" style="border-top-color:var(--azul-profundo)">
          <div class="card-image" style="background:linear-gradient(135deg,var(--azul-profundo),var(--azul-cielo))">
            <i :class="vid.icon"></i>
          </div>
          <div class="card-content">
            <h4 class="card-title">{{ vid.title }}</h4>
            <p class="card-text">{{ vid.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from '@/composables/useToast';
import { useScrollReveal } from '@/composables/useAnimations';

const sectionRef = ref(null);
const { registerReveal } = useScrollReveal();
onMounted(() => registerReveal(sectionRef.value));

const { showToast } = useToast();
const activeTab = ref('newsletter');

const tabs = [
  { id: 'newsletter', label: 'Boletines', emoji: '📰' },
  { id: 'blog', label: 'Blog', emoji: '📚' },
  { id: 'activities', label: 'Actividades', emoji: '🎯' },
  { id: 'podcasts', label: 'Podcasts', emoji: '🎧' },
  { id: 'videos', label: 'Videos', emoji: '🎬' },
];

const newsletters = [
  { icon: '📧', title: 'Boletín Semanal', description: 'Recibe inspiración, noticias y actualizaciones cada semana. Suscríbete para mantenerte conectado con nuestra comunidad.', action: 'Suscribirse', toast: '¡Suscrito al boletín semanal!' },
  { icon: '🔔', title: 'Notificaciones', description: 'Recibe alertas sobre eventos especiales, retiros espirituales y oportunidades de servicio. No te pierdas nada importante.', action: 'Activar', toast: 'Notificaciones activadas' },
  { icon: '✨', title: 'Reflexión Diaria', description: 'Cada mañana un mensaje de esperanza y fortaleza para tu día. Reflexiones cortas que transforman tu perspectiva.', action: 'Suscribirse', toast: '¡Suscrito a reflexiones diarias!' },
];

const blogPosts = [
  { date: '15 de Diciembre, 2024', title: 'El Poder de la Comunidad', excerpt: 'Descubre cómo la vida en comunidad transforma nuestro camino espiritual y nos fortalece en la fe.', gradient: 'linear-gradient(135deg, var(--vh-azul-cielo), var(--vh-dorado))', icon: 'fas fa-pen-fancy' },
  { date: '12 de Diciembre, 2024', title: 'Servicio con Amor', excerpt: 'Exploramos cómo el servicio genuino es la expresión más pura del amor que predicamos.', gradient: 'linear-gradient(135deg, var(--vh-rosa), var(--vh-dorado))', icon: 'fas fa-heart' },
  { date: '08 de Diciembre, 2024', title: 'Liderazgo que Impacta', excerpt: 'Reflexiones sobre cómo desarrollar un liderazgo que realmente transforma vidas y comunidades.', gradient: 'linear-gradient(135deg, var(--vh-verde), var(--vh-azul-cielo))', icon: 'fas fa-light-bulb' },
];

const activities = [
  { tag: 'Completada', tagColor: 'var(--vh-rosa)', title: 'Retiro Espiritual 2024', description: 'Tres días de oración, comunidad y encuentro con Dios en la montaña. ¡Un evento transformador!', gradient: 'linear-gradient(135deg, var(--vh-verde), var(--vh-azul-cielo))', icon: 'fas fa-users' },
  { tag: 'En curso', tagColor: 'var(--vh-verde)', title: 'Proyecto Social', description: 'Estamos construyendo esperanza en el barrio. Participa en nuestro programa de desarrollo comunitario.', gradient: 'linear-gradient(135deg, var(--vh-azul-profundo), var(--vh-azul-cielo))', icon: 'fas fa-handshake' },
  { tag: 'Próximo', tagColor: 'var(--vh-azul-cielo)', title: 'Escuela de Liderazgo', description: 'Inicia el 15 de enero. Seis módulos para equiparte como líder de impacto en tu área.', gradient: 'linear-gradient(135deg, var(--vh-dorado), var(--vh-rosa))', icon: 'fas fa-graduation-cap' },
];

const podcasts = [
  { duration: '25 min', title: 'Reflexiones de Fe', description: 'Mensajes diarios que transforman tu perspectiva sobre la vida, la fe y el propósito.', icon: 'fas fa-podcast' },
  { duration: '45 min', title: 'Historias de Transformación', description: 'Testimonios reales de personas cuyas vidas han sido radicalmente transformadas por fe.', icon: 'fas fa-microphone' },
  { duration: '35 min', title: 'Liderazgo que Impacta', description: 'Conversaciones profundas sobre cómo desarrollar liderazgo transformacional en tu esfera.', icon: 'fas fa-headphones' },
];

const videos = [
  { title: 'Cortometraje Testimonios', description: 'Historias reales de transformación y fe. Escúchalos compartir su experiencia.', icon: 'fas fa-play-circle' },
  { title: 'Conferencias Destacadas', description: 'Enseñanzas profundas de nuestros pastores sobre fe, propósito y comunidad.', icon: 'fas fa-video' },
  { title: 'Reels & Shorts', description: 'Contenido dinámico, divertido e inspirador. Mensajes poderosos en poco tiempo.', icon: 'fas fa-film' },
];

function playMedia(title) {
  showToast('▶ Reproduciendo: ' + title, 'info');
}
</script>

<style scoped>
.section { padding: 7rem 5%; }
.section-header { text-align: center; margin-bottom: 5rem; }
.section-label {
  display: inline-block; color: var(--vh-dorado);
  font-weight: 700; font-size: 0.8rem;
  text-transform: uppercase; letter-spacing: 3px;
  margin-bottom: 1rem; font-family: 'Barlow', sans-serif;
}
.section-title {
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  color: var(--vh-azul-profundo);
  margin-bottom: 1.2rem; line-height: 1.1;
}
.section-subtitle {
  color: #666; max-width: 650px;
  margin: 0 auto; font-size: 1.15rem; line-height: 1.8;
}
.content-tabs {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}
.tab-btn {
  padding: 0.8rem 2rem;
  border: none;
  background: transparent;
  font-family: 'Barlow', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: var(--vh-transicion);
  border-bottom: 3px solid transparent;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 8px 8px 0 0;
}
.tab-btn.active {
  color: var(--vh-azul-profundo);
  border-bottom-color: var(--vh-dorado);
  background: rgba(251, 166, 54, 0.05);
}
.tab-btn:hover {
  color: var(--vh-azul-profundo);
  background: rgba(26, 75, 140, 0.05);
}
.tab-content { animation: fadeInUp 0.5s ease; }

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}
.card-elevated {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 5px 25px var(--vh-sombra);
  transition: var(--vh-transicion);
  border-top: 4px solid var(--vh-dorado);
  animation: bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.card-elevated:nth-child(2) { animation-delay: 0.1s; }
.card-elevated:nth-child(3) { animation-delay: 0.2s; }
.card-elevated:hover {
  transform: translateY(-12px) scale(1.01);
  box-shadow: 0 15px 50px var(--vh-sombra);
}
.card-image {
  width: 100%; height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  position: relative;
  overflow: hidden;
}
.card-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%);
  animation: shimmer 2s infinite;
}
.card-content { padding: 2rem; }
.card-tag {
  display: inline-block;
  padding: 0.4rem 1rem;
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
}
.card-date {
  color: var(--vh-dorado);
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.card-title {
  font-size: 1.2rem;
  color: var(--vh-azul-profundo);
  margin-bottom: 0.8rem;
}
.card-text {
  color: #555;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}
.card-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.8rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  transition: var(--vh-transicion);
  border: none;
  cursor: pointer;
}
.card-action:hover { transform: translateY(-2px); }
.read-more {
  color: var(--vh-azul-profundo);
  font-weight: 700;
  text-decoration: none;
  transition: color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.read-more:hover { color: var(--vh-dorado); }
.newsletter-icon { font-size: 3rem; margin-bottom: 1rem; }

@media (max-width: 768px) {
  .content-tabs { gap: 0.5rem; }
  .tab-btn { padding: 0.6rem 1.2rem; font-size: 0.85rem; }
}
</style>
